import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, ShieldCheck, Truck, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { createCheckout, EUR, API } from "@/lib/api";

const inputCls =
    "w-full rounded-xl border border-sage/20 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink/30 outline-none transition focus:border-sage focus:ring-2 focus:ring-sage/20";

export default function CheckoutDrawer({ pack, onClose }) {
    const [loading, setLoading] = useState(false);
    const [stripeMode, setStripeMode] = useState("test");
    const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", postal: "", city: "" });
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    useEffect(() => {
        fetch(`${API}/config`)
            .then((r) => r.json())
            .then((d) => setStripeMode(d.stripe_mode || "test"))
            .catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!pack) return;
        setLoading(true);
        try {
            const data = await createCheckout(pack, form);
            window.location.href = data.checkout_url;
        } catch (err) {
            toast.error("Une erreur est survenue lors de la création du paiement. Veuillez réessayer.");
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {pack && (
                <>
                    <motion.div
                        data-testid="checkout-backdrop"
                        className="fixed inset-0 z-[60] bg-ink/50 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                    />
                    <motion.aside
                        data-testid="checkout-drawer"
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 32, stiffness: 320 }}
                        className="fixed right-0 top-0 z-[70] h-full w-full max-w-md bg-cream shadow-2xl flex flex-col"
                    >
                        <div className="flex items-center justify-between px-6 py-5 border-b border-sage/10">
                            <h2 className="font-serif text-2xl text-ink">Finaliser ma commande</h2>
                            <button
                                data-testid="checkout-close-button"
                                onClick={onClose}
                                aria-label="Fermer"
                                className="p-2 rounded-full hover:bg-sage-soft text-ink transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-6">
                            <div data-testid="checkout-summary-pack" className="rounded-2xl bg-white border border-sage/15 p-5">
                                <div className="flex items-center justify-between gap-4">
                                    <span className="font-semibold text-sm text-ink">{pack.name}</span>
                                    <span className="font-serif font-bold text-2xl text-ink">{EUR(pack.price)}</span>
                                </div>
                                <div className="mt-1 flex items-center justify-between gap-4 text-sm">
                                    <span className="line-through text-ink/35">{EUR(pack.original)}</span>
                                    {pack.discount && <span className="rounded-full bg-terra-soft text-terra px-2.5 py-0.5 text-xs font-bold">{pack.discount}</span>}
                                </div>
                                <div className="mt-3 pt-3 border-t border-sage/10 flex items-center gap-2 text-xs text-sage font-medium">
                                    <Truck size={14} /> Livraison suivie 8-12 jours — Offerte
                                </div>
                            </div>
                            <form data-testid="checkout-form" onSubmit={submit} className="mt-6 flex flex-col gap-4">
                                <div>
                                    <label className="text-xs font-semibold text-ink/70 mb-1.5 block">Nom complet</label>
                                    <input data-testid="checkout-field-name" required value={form.name} onChange={set("name")} placeholder="Marie Dupont" className={inputCls} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-ink/70 mb-1.5 block">E-mail</label>
                                    <input data-testid="checkout-field-email" required type="email" value={form.email} onChange={set("email")} placeholder="marie@exemple.fr" className={inputCls} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-ink/70 mb-1.5 block">Téléphone (optionnel)</label>
                                    <input data-testid="checkout-field-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78" className={inputCls} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-ink/70 mb-1.5 block">Adresse de livraison</label>
                                    <input data-testid="checkout-field-address" required value={form.address} onChange={set("address")} placeholder="12 rue des Lilas" className={inputCls} />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs font-semibold text-ink/70 mb-1.5 block">Code postal</label>
                                        <input data-testid="checkout-field-postal" required value={form.postal} onChange={set("postal")} placeholder="75002" className={inputCls} />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-ink/70 mb-1.5 block">Ville</label>
                                        <input data-testid="checkout-field-city" required value={form.city} onChange={set("city")} placeholder="Paris" className={inputCls} />
                                    </div>
                                </div>
                                {stripeMode === "test" && (
                                <div className="mt-2 flex items-start gap-2.5 rounded-xl bg-terra-soft p-4 text-xs text-ink/80 leading-relaxed" data-testid="checkout-test-notice">
                                    <CreditCard size={16} className="shrink-0 text-terra mt-0.5" />
                                    <span>
                                        <strong>Mode démo :</strong> paiement Stripe en environnement de test. Utilisez la carte{" "}
                                        <span className="font-mono font-semibold">4242 4242 4242 4242</span> — date d’expiration et CVC au choix. Aucun montant réel ne sera débité.
                                    </span>
                                </div>
                                )}
                                <button
                                    data-testid="checkout-form-submit-button"
                                    type="submit"
                                    disabled={loading}
                                    className="mt-1 w-full rounded-full bg-sage hover:bg-sage-hover disabled:opacity-60 text-cream font-semibold py-4 flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.01]"
                                >
                                    {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />}
                                    {loading ? "Redirection sécurisée…" : `Payer ${EUR(pack.price)} en toute sécurité`}
                                </button>
                            </form>
                            <div className="mt-6 grid grid-cols-1 gap-2 text-xs text-ink/60">
                                <span className="flex items-center gap-2"><Lock size={13} className="text-sage" /> Paiement sécurisé — Carte bancaire & PayPal</span>
                                <span className="flex items-center gap-2"><ShieldCheck size={13} className="text-sage" /> Satisfait ou Remboursé 30 jours</span>
                                <span className="flex items-center gap-2"><Truck size={13} className="text-sage" /> Colis suivi — livraison 8 à 12 jours</span>
                            </div>
                        </div>
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
}
